import { useEffect, useState } from "react";
import { ArrowLeft, FileDown, Pencil, TrendingUp, SlidersHorizontal } from "lucide-react";
import { toast } from "sonner";
import { api, downloadFile, money } from "../api";
import { useAuth } from "../auth";
import NewProjectModal from "../components/NewProjectModal";
import QuickPaymentModal from "../components/QuickPaymentModal";
import WorkItemsPanel from "../components/WorkItemsPanel";
import PaymentsPanel from "../components/PaymentsPanel";
import AlertsPanel from "../components/AlertsPanel";
import PriceAdjustModal from "../components/PriceAdjustModal";
import { PriceHistory } from "../components/PriceHistory";
import MasterEditModal from "../components/MasterEditModal";

export default function ProjectDetail({ id, onBack }) {
  const { can } = useAuth();
  const [prj, setPrj] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [quick, setQuick] = useState(null);
  const [priceOpen, setPriceOpen] = useState(false);
  const [masterOpen, setMasterOpen] = useState(false);

  const load = () => api.get(`/projects/${id}`).then((r) => setPrj(r.data)).catch(() => toast.error("Proje yüklenemedi"));
  useEffect(() => { load(); }, [id]);

  const pdf = async () => {
    try { await downloadFile(`/reports/pdf/project/${id}`, `${prj.name}-rapor.pdf`); toast.success("PDF indirildi"); }
    catch { toast.error("PDF oluşturulamadı"); }
  };

  if (!prj) return <main className="content"><p>Yükleniyor…</p></main>;
  const remaining = (prj.agreed_amount || 0) - (prj.paid_amount || 0);
  const hasAlerts = prj.alerts && (prj.alerts.counts.overdue + prj.alerts.counts.soon + prj.alerts.counts.upcoming) > 0;

  return (
    <main className="content" data-testid="page-project-detail">
      <button className="link-back" onClick={onBack} data-testid="back-button"><ArrowLeft size={14} /> Projelere dön</button>
      <div className="page-head">
        <div>
          <span className="section-kicker">PROJE #{prj.project_no || "-"} · {prj.employer_short}{prj.investor_short && prj.investor_short !== prj.employer_short ? ` / Yatırımcı: ${prj.investor_short}` : ""}</span>
          <h1>{prj.name}</h1>
          <p>{prj.city || "—"} · {prj.district || ""} · {prj.capacity || ""} · Sorumlu: {prj.responsible || "—"}</p>
        </div>
        <div className="head-actions">
          <em className={`status ${prj.status === "Teklif" ? "status-blue" : prj.status === "Tamamlandı" ? "status-green" : "status-amber"}`}>{prj.status}</em>
          <button className="import-btn" onClick={pdf} data-testid="project-pdf-button"><FileDown size={15} /> PDF Rapor</button>
          {can("admin", "project_manager") && <button className="import-btn" onClick={() => setPriceOpen(true)} data-testid="project-price-adjust-button"><TrendingUp size={15} /> Fiyat Farkı</button>}
          {can("admin", "project_manager") && <button className="new-btn" onClick={() => setEditOpen(true)} data-testid="edit-project-button"><Pencil size={15} /> Düzenle</button>}
        </div>
      </div>

      <div className="stats-grid">
        <div className="stat"><div><div className="eyebrow">Gelir (Beklenen)</div><strong>{money(prj.revenue_try, "TRY")}</strong><span className="stat-detail">{prj.deal_type === "itemized" ? "İş kalemi bazlı" : `Kırılımsız · ${money(prj.agreed_amount, prj.currency)}`}</span></div></div>
        <div className="stat"><div><div className="eyebrow">Alt Yüklenici Maliyeti</div><strong>{money(prj.cost_try, "TRY")}</strong><span className="stat-detail">{prj.work_items.length} kalem</span></div></div>
        <div className="stat"><div><div className="eyebrow">Beklenen Kâr</div><strong className={prj.profit_try >= 0 ? "profit-pos" : "profit-neg"} data-testid="project-profit">{money(prj.profit_try, "TRY")}</strong><span className="stat-detail">{prj.revenue_try ? `Marj %${Math.round(prj.profit_try / prj.revenue_try * 100)}` : "Gelir − maliyet"}</span></div></div>
        <div className="stat"><div><div className="eyebrow">Tahsil Edilen</div><strong>{money(prj.paid_amount, prj.currency)}</strong><span className="stat-detail">Kalan tahsilat {money(remaining, prj.currency)}</span></div></div>
      </div>
      {prj.rates_missing && <div className="empty-hint small" data-testid="rates-missing-warn">⚠ Bazı döviz kurları eksik olduğundan TL karşılıkları eksik olabilir. Kurları "Finans & Raporlar" bölümünden ekleyin.</div>}

      {prj.price_history?.length > 0 && (
        <section className="panel" data-testid="project-price-history-panel">
          <div className="panel-head"><div><span className="eyebrow">SÖZLEŞME BEDELİ</span><h2>Fiyat Değişim Geçmişi</h2></div></div>
          <PriceHistory history={prj.price_history} currency={prj.currency} testid="project-price-history" />
        </section>
      )}

      {(() => {
        const hasMeta = prj.project_type || prj.city || prj.license_no || prj.capacity_production || prj.capacity_storage || prj.turbine_count || prj.process_status || prj.locality;
        const editable = can("admin", "project_manager");
        if (!hasMeta && !editable) return null;
        return (
          <section className="panel" data-testid="project-tech-panel">
            <div className="panel-head">
              <div><span className="eyebrow">KÜNYE</span><h2>Teknik & Lisans Bilgileri</h2></div>
              {editable && <button className="import-btn" onClick={() => setMasterOpen(true)} data-testid="edit-master-button"><SlidersHorizontal size={14} /> Düzenle</button>}
            </div>
            {hasMeta ? (
              <div className="tech-grid">
                {prj.project_type && <div className="tech-item"><small>TÜR</small><b>{prj.project_type}</b></div>}
                {prj.process_status && <div className="tech-item"><small>DURUM</small><b>{prj.process_status}</b></div>}
                {(prj.city || prj.district || prj.locality) && <div className="tech-item"><small>KONUM</small><b>{[prj.city, prj.district, prj.locality].filter(Boolean).join(" / ")}</b></div>}
                {prj.capacity_production && <div className="tech-item"><small>ÜRETİM KAPASİTESİ</small><b>{prj.capacity_production}</b></div>}
                {prj.capacity_storage && <div className="tech-item"><small>DEPOLAMA KAPASİTESİ</small><b>{prj.capacity_storage}</b></div>}
                {prj.turbine_count && <div className="tech-item"><small>TÜRBİN SAYISI</small><b>{prj.turbine_count}</b></div>}
                {prj.license_no && <div className="tech-item"><small>LİSANS BİLGİSİ</small><b>{prj.license_no}</b></div>}
              </div>
            ) : (
              <div className="empty-hint small" data-testid="tech-empty">Künye bilgisi girilmemiş. "Düzenle" ile tür, konum, lisans ve kapasite bilgilerini ekleyin.</div>
            )}
          </section>
        );
      })()}

      {hasAlerts && <AlertsPanel alerts={prj.alerts} compact />}
      <WorkItemsPanel prj={prj} reload={load} onQuickPay={setQuick} />
      <PaymentsPanel prj={prj} reload={load} onQuickPay={setQuick} />

      {editOpen && <NewProjectModal project={prj} close={() => setEditOpen(false)} refresh={load} />}
      {quick && <QuickPaymentModal preset={quick} close={() => setQuick(null)} onSaved={load} />}
      {priceOpen && <PriceAdjustModal target={{ kind: "project", id: prj.id, name: `${prj.name} · Sözleşme Bedeli`, current: prj.agreed_amount, currency: prj.currency }} close={() => setPriceOpen(false)} onSaved={load} />}
      {masterOpen && <MasterEditModal project={prj} close={() => setMasterOpen(false)} onSaved={load} />}
    </main>
  );
}
